import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Check, Award, Users, Target, Heart, ArrowRight, Clock, TrendingUp, CheckCircle, Sparkles, PenTool, BookOpen, Mic, FileEdit, Palette, Star } from 'lucide-react';
import SEO from '../components/SEO';
import ServicePopup from '../components/ServicePopup';
import { openChat } from '../utils/chatUtils';
import { portfolioProjects, testimonials, services } from '../mock';
import AOS from 'aos';
import 'aos/dist/aos.css';

const AboutUs = () => {
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupService, setPopupService] = useState({ title: '', description: '' });
  const [activeGenre, setActiveGenre] = useState('Children\'s Book');

  const openServicePopup = (title, description) => {
    setPopupService({ title, description });
    setPopupOpen(true);
  };

  const genres = ['Children\'s Book', 'Fiction', 'Memoir', 'Non-Fiction', 'Poetry'];
  const filteredProjects = portfolioProjects.filter(project => project.genre === activeGenre);

  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: 'ease-out',
      once: true,
      offset: 100
    });

    // Parallax effect for hero background
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      const heroBackground = document.querySelector('.hero-background');
      if (heroBackground) {
        heroBackground.style.transform = `translateY(${scrolled * 0.5}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.scrollTo(0, 0);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#000000]">
      <SEO 
        title="About Us - White House Publishers"
        description="Learn about White House Publishers - Proven publishing excellence, 500+ books published, and a team dedicated to helping authors succeed. Discover our story and values."
        keywords="about White House Publishers, book publishing company, publishing services, professional editors, book design team, publishing history"
        url="/about"
      />
      
      {/* Hero Section with Animated Background */}
      <section className="hero-section-split relative min-h-[70vh] flex items-center py-12 sm:py-16 md:py-20 mt-20 sm:mt-24">
        <div className="hero-background">
          <img
            src="https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1920"
            alt="About White House Publishers"
            className="hero-image"
          />
          <div className="hero-overlay-split"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center" data-aos="fade-up">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#F4D03F] mb-4 sm:mb-6 leading-tight uppercase">
              WHITE HOUSE PUBLISHERS
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-[#dfddd6] mb-6 sm:mb-8 leading-relaxed">
              Transforming manuscripts into masterpieces with proven expertise. We connect you with expert writers and publishing services to bring your story to life.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button 
                onClick={() => openServicePopup('Get Started with Us', 'Join 500+ authors who have published with us. Let\'s discuss how we can help bring your book to life.')}
                className="h-12 sm:h-14 px-6 sm:px-8 rounded-xl bg-[#F4D03F] text-[#0B0B0B] font-semibold text-sm sm:text-base tracking-[0.2px] hover:bg-[#F8D26B] transition-all duration-300 flex items-center justify-center gap-2 group w-full sm:w-auto"
              >
                Start Your Journey
                <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
              </button>
              <button 
                onClick={openChat}
                className="js-chat h-12 sm:h-14 px-6 sm:px-8 rounded-xl border-2 border-[#F4D03F] text-[#F4D03F] font-semibold text-sm sm:text-base tracking-[0.2px] hover:bg-[rgba(244,208,63,0.1)] transition-all duration-300 flex items-center justify-center gap-2 group w-full sm:w-auto"
              >
                Discuss Your Project
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-16 sm:py-20 bg-[#302f2c]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center">
              {/* Left Side - Content */}
              <div data-aos="fade-right">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#F4D03F] mb-4 sm:mb-6 uppercase">Who We Are?</h2>
                <p className="text-[#dfddd6] text-base sm:text-lg mb-4 leading-relaxed">
                  At White House Publishers, we specialize in pairing authors with expert writers who capture your unique vision. Our experienced team ensures your story speaks directly to readers, creating an unforgettable impact.
                </p>
                <p className="text-[#dfddd6] text-base sm:text-lg mb-6 leading-relaxed">
                  We focus on providing personalized strategies that highlight your voice and deliver success. Whether fiction or non-fiction, we guide you every step of the way, making your book a true reflection of your ideas.
                </p>
                
                <button 
                  onClick={() => openServicePopup('Discuss Your Project', 'Let\'s work together to bring your book vision to life. Our expert team is ready to help you succeed.')}
                  className="h-14 sm:h-14 px-6 sm:px-8 rounded-xl bg-[#F4D03F] text-[#0B0B0B] font-semibold text-sm sm:text-base tracking-[0.2px] hover:bg-[#F8D26B] transition-all duration-300 flex items-center justify-center gap-2 group"
                >
                  Discuss Project
                  <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
                </button>
              </div>
              
              {/* Right Side - Image */}
              <div data-aos="fade-left">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1519682337058-a94d519337bc?w=800"
                    alt="Who We Are - White House Publishers Team"
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section className="py-16 sm:py-20 bg-[#000000]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#F4D03F] mb-4 uppercase">Our Portfolio</h2>
            <p className="text-base sm:text-lg text-[#dfddd6] max-w-2xl mx-auto">
              We don't just talk the talk, we write it. Take a look at some of our latest collaborations with authors across genres.
            </p>
          </div>

          {/* Genre Tabs */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-12" data-aos="fade-up" data-aos-delay="100">
            {genres.map((genre) => (
              <button
                key={genre}
                onClick={() => setActiveGenre(genre)}
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold text-xs sm:text-sm uppercase tracking-wide transition-all duration-300 ${
                  activeGenre === genre
                    ? 'bg-[#F4D03F] text-[#000000] shadow-lg scale-105'
                    : 'bg-[#2C3E50] text-[#ffffff] hover:bg-[#F8D26B] hover:text-[#000000]'
                }`}
              >
                {genre}
              </button>
            ))}
          </div>

          {/* Books Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 sm:gap-6">
            {filteredProjects.slice(0, 6).map((project, index) => (
              <div
                key={project.id}
                className="portfolio-book-card group"
                data-aos="zoom-in"
                data-aos-delay={index * 50}
              >
                <Card className="bg-[#2C3E50] border-[#F8D26B] border-opacity-30 overflow-hidden h-full hover:border-[#F4D03F] transition-all duration-500 hover:shadow-2xl hover:shadow-[#F4D03F]/20">
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {/* Genre Badge */}
                    <div className="absolute top-2 left-2 bg-[#F8D26B] text-[#000000] px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase">
                      {project.genre}
                    </div>
                  </div>
                  <CardContent className="p-2 sm:p-3">
                    <h3 className="text-[#ffffff] font-bold text-xs sm:text-sm mb-1 group-hover:text-[#F4D03F] transition-colors truncate">
                      {project.title}
                    </h3>
                    <p className="text-[#F4D03F] text-[10px] sm:text-xs truncate">{project.author}</p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 sm:py-20 bg-[#302f2c]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#F4D03F] mb-4 uppercase">Our Services</h2>
            <p className="text-base sm:text-lg text-[#888680] max-w-2xl mx-auto">
              From memoirs to business books, we provide comprehensive publishing services with purpose.
            </p>
          </div>

          <div className="space-y-12 sm:space-y-16 max-w-6xl mx-auto">
            {/* Book Writing Service */}
            <div className="grid md:grid-cols-2 gap-8 items-center" data-aos="fade-up">
              <div className="order-2 md:order-1">
                <div className="relative rounded-xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1455390582262-044cdead277a?w=800"
                    alt="Book Writing Services"
                    className="w-full h-64 sm:h-80 object-cover"
                  />
                </div>
              </div>
              <div className="order-1 md:order-2">
                <h3 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-3">Book Writing</h3>
                <p className="text-[#dfddd6] text-sm sm:text-base mb-4 leading-relaxed">
                  Do you have a brilliant story idea but find it challenging to put into words? Our talented writers will work closely with you to transform your concept into a compelling manuscript.
                </p>
                <div className="flex gap-3">
                  <button 
                    onClick={() => openServicePopup('Book Writing Service', 'From initial brainstorming to the final draft, we ensure that every word reflects your unique voice and vision.')}
                    className="h-11 sm:h-11 px-4 sm:px-6 rounded-lg bg-[#F4D03F] text-[#0B0B0B] font-semibold text-xs sm:text-sm hover:bg-[#F8D26B] transition-all duration-300"
                  >
                    Discuss Project
                  </button>
                  <button 
                    onClick={openChat}
                    className="h-11 sm:h-11 px-4 sm:px-6 rounded-lg border-2 border-[#F4D03F] text-[#F4D03F] font-semibold text-xs sm:text-sm hover:bg-[rgba(244,208,63,0.1)] transition-all duration-300"
                  >
                    Live Chat
                  </button>
                </div>
              </div>
            </div>

            {/* Book Publishing Service */}
            <div className="grid md:grid-cols-2 gap-8 items-center" data-aos="fade-up">
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-3">Book Publishing</h3>
                <p className="text-[#dfddd6] text-sm sm:text-base mb-4 leading-relaxed">
                  Turning your manuscript into a published book is a journey we're excited to help you navigate. Our publishing experts take care of every detail, from securing an ISBN to preparing your book for launch.
                </p>
                <div className="flex gap-3">
                  <button 
                    onClick={() => openServicePopup('Book Publishing Service', 'We handle the technicalities, ensuring your book is formatted properly and ready to captivate audiences worldwide.')}
                    className="h-11 sm:h-11 px-4 sm:px-6 rounded-lg bg-[#F4D03F] text-[#0B0B0B] font-semibold text-xs sm:text-sm hover:bg-[#F8D26B] transition-all duration-300"
                  >
                    Discuss Project
                  </button>
                  <button 
                    onClick={openChat}
                    className="h-11 sm:h-11 px-4 sm:px-6 rounded-lg border-2 border-[#F4D03F] text-[#F4D03F] font-semibold text-xs sm:text-sm hover:bg-[rgba(244,208,63,0.1)] transition-all duration-300"
                  >
                    Live Chat
                  </button>
                </div>
              </div>
              <div>
                <div className="relative rounded-xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1512820790803-83ca734da794?w=800"
                    alt="Book Publishing Services"
                    className="w-full h-64 sm:h-80 object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Book Editing Service */}
            <div className="grid md:grid-cols-2 gap-8 items-center" data-aos="fade-up">
              <div className="order-2 md:order-1">
                <div className="relative rounded-xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=800"
                    alt="Book Editing Services"
                    className="w-full h-64 sm:h-80 object-cover"
                  />
                </div>
              </div>
              <div className="order-1 md:order-2">
                <h3 className="text-2xl sm:text-3xl font-bold text-[#F4D03F] mb-3">Book Editing</h3>
                <p className="text-[#dfddd6] text-sm sm:text-base mb-4 leading-relaxed">
                  Every word matters, and editing is where the magic happens. Our skilled editors provide comprehensive services that refine your manuscript's clarity, flow, and consistency.
                </p>
                <div className="flex gap-3">
                  <button 
                    onClick={() => openServicePopup('Book Editing Service', 'From developmental editing to meticulous proofreading, we ensure your manuscript reflects the best version of your story.')}
                    className="h-11 sm:h-11 px-4 sm:px-6 rounded-lg bg-[#F4D03F] text-[#0B0B0B] font-semibold text-xs sm:text-sm hover:bg-[#F8D26B] transition-all duration-300"
                  >
                    Discuss Project
                  </button>
                  <button 
                    onClick={openChat}
                    className="h-11 sm:h-11 px-4 sm:px-6 rounded-lg border-2 border-[#F4D03F] text-[#F4D03F] font-semibold text-xs sm:text-sm hover:bg-[rgba(244,208,63,0.1)] transition-all duration-300"
                  >
                    Live Chat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Story Section */}
      <section className="py-16 sm:py-20 bg-[#000000] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, #D4AF37 0px, #D4AF37 1px, transparent 1px, transparent 12px)',
            backgroundSize: '17px 17px'
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center">
              {/* Left Side - Image */}
              <div data-aos="fade-right">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=800"
                    alt="White House Publishers Story"
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                </div>
              </div>
              
              {/* Right Side - Content */}
              <div data-aos="fade-left">
                <h2 className="text-3xl sm:text-4xl font-bold text-[#F4D03F] mb-4 uppercase">White House Publishers Brings Every Story To Life</h2>
                <p className="text-[#dfddd6] text-base sm:text-lg mb-4 leading-relaxed">
                  At White House Publishers, we embrace the diversity of storytelling. Whether it's captivating fiction, insightful non-fiction, or something entirely unique, we work alongside you to transform your ideas into powerful narratives that resonate with readers.
                </p>
                <p className="text-[#888680] text-sm sm:text-base mb-6 leading-relaxed">
                  Our team combines creative vision with expert craftsmanship to ensure your story shines. Whatever your genre, we're dedicated to bringing your vision to life, creating something extraordinary that leaves a lasting impact. Let's make your story unforgettable together.
                </p>
                
                <button 
                  onClick={() => openServicePopup('Start Your Story', 'Let\'s craft a story that captivates your readers and leaves a lasting impact.')}
                  className="h-14 sm:h-14 px-6 sm:px-8 rounded-xl bg-[#F4D03F] text-[#0B0B0B] font-semibold text-sm sm:text-base tracking-[0.2px] hover:bg-[#F8D26B] transition-all duration-300 flex items-center justify-center gap-2 group"
                >
                  Discuss Project
                  <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits/USPs Section */}
      <section className="py-16 sm:py-20 bg-[#302f2c]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#F4D03F] mb-4 uppercase">Why Choose Us?</h2>
            <p className="text-base sm:text-lg text-[#888680] max-w-2xl mx-auto">
              We provide comprehensive support and expertise to help you succeed
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-6xl mx-auto">
            <Card className="bg-[#000000] border-[#3f4816] hover:border-[#F4D03F] transition-all duration-300" data-aos="fade-up" data-aos-delay="0">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#F4D03F]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="text-[#F4D03F]" size={28} />
                </div>
                <h3 className="text-[#F4D03F] font-bold text-lg mb-3">Time Freedom</h3>
                <p className="text-[#888680] text-sm leading-relaxed">
                  You focus on what matters; ideas, while our writers handle the writing.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#000000] border-[#3f4816] hover:border-[#F4D03F] transition-all duration-300" data-aos="fade-up" data-aos-delay="100">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#F4D03F]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="text-[#F4D03F]" size={28} />
                </div>
                <h3 className="text-[#F4D03F] font-bold text-lg mb-3">Expertise Advantage</h3>
                <p className="text-[#888680] text-sm leading-relaxed">
                  Access seasoned professionals in genres from memoirs to business writing.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#000000] border-[#3f4816] hover:border-[#F4D03F] transition-all duration-300" data-aos="fade-up" data-aos-delay="200">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#F4D03F]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="text-[#F4D03F]" size={28} />
                </div>
                <h3 className="text-[#F4D03F] font-bold text-lg mb-3">Proven Results</h3>
                <p className="text-[#888680] text-sm leading-relaxed">
                  Our authors become published, with polished, compelling manuscripts.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#000000] border-[#3f4816] hover:border-[#F4D03F] transition-all duration-300" data-aos="fade-up" data-aos-delay="300">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#F4D03F]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="text-[#F4D03F]" size={28} />
                </div>
                <h3 className="text-[#F4D03F] font-bold text-lg mb-3">Stress Free Process</h3>
                <p className="text-[#888680] text-sm leading-relaxed">
                  From outline to final draft, we manage every step so you can relax.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Client Success / Testimonials Section */}
      <section className="py-16 sm:py-20 bg-[#000000]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#F4D03F] mb-4 uppercase">Client Success</h2>
            <p className="text-base sm:text-lg text-[#888680] max-w-3xl mx-auto mb-6">
              Let's be honest, you can market the hell out of a mediocre book, but if it's not well-written, it's not going to sell. So if you're serious about publishing a bestselling book, make sure you give your project the strongest start by working with one of the best publishing services in the business.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {testimonials.slice(0, 6).map((testimonial, index) => (
              <Card key={testimonial.id} className="bg-[#302f2c] border-[#3f4816] hover:border-[#F4D03F] transition-all duration-300" data-aos="fade-up" data-aos-delay={index * 100}>
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={16} className="fill-[#F4D03F] text-[#F4D03F]" />
                    ))}
                  </div>
                  <p className="text-[#dfddd6] mb-4 italic text-sm leading-relaxed">"{testimonial.content}"</p>
                  <div className="border-t border-[#3f4816] pt-4">
                    <div className="text-[#F4D03F] font-bold text-sm">{testimonial.author}</div>
                    <div className="text-[#888680] text-xs">{testimonial.role}</div>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-[#F4D03F] text-xs font-semibold">★ 4.5/5</span>
                    <span className="text-[#888680] text-xs">Rated On Trustpilot</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats/Expertise Section */}
      <section className="py-16 sm:py-20 bg-[#302f2c]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12 max-w-5xl mx-auto">
            <div className="text-center" data-aos="zoom-in" data-aos-delay="0">
              <div className="w-24 h-24 sm:w-28 sm:h-28 bg-[#F4D03F]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-[#F4D03F] text-3xl sm:text-4xl font-bold">98%</span>
              </div>
              <h3 className="text-[#F4D03F] font-bold text-xl sm:text-2xl mb-2">Client Satisfaction</h3>
              <p className="text-[#888680] text-sm sm:text-base">Expert guidance at every step</p>
            </div>
            <div className="text-center" data-aos="zoom-in" data-aos-delay="100">
              <div className="w-24 h-24 sm:w-28 sm:h-28 bg-[#F4D03F]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-[#F4D03F] text-3xl sm:text-4xl font-bold">500+</span>
              </div>
              <h3 className="text-[#F4D03F] font-bold text-xl sm:text-2xl mb-2">Books Published</h3>
              <p className="text-[#888680] text-sm sm:text-base">Helping authors share their stories worldwide</p>
            </div>
            <div className="text-center" data-aos="zoom-in" data-aos-delay="200">
              <div className="w-24 h-24 sm:w-28 sm:h-28 bg-[#F4D03F]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-[#F4D03F] text-3xl sm:text-4xl font-bold">150+</span>
              </div>
              <h3 className="text-[#F4D03F] font-bold text-xl sm:text-2xl mb-2">Countries</h3>
              <p className="text-[#888680] text-sm sm:text-base">Global distribution network</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 sm:py-20 relative overflow-hidden" style={{ 
        background: 'linear-gradient(135deg, #1a0a0f 0%, #2d1319 25%, #3d1820 40%, #2d1319 60%, #1a0a0f 80%, #0a0a0a 100%)',
        backgroundSize: '200% 200%'
      }}>
        {/* Animated diagonal stripe pattern overlay */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'repeating-linear-gradient(45deg, #D4AF37 0px, #D4AF37 2px, transparent 2px, transparent 20px)',
          backgroundSize: '28px 28px'
        }}></div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div data-aos="fade-right">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                  Ready to Bring Your Story to Life?
                </h2>
                <p className="text-[#dfddd6] text-base sm:text-lg mb-6 leading-relaxed">
                  Let's craft a story that captivates your readers and leaves a lasting impact.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <button 
                    onClick={() => openServicePopup('Start Your Book Journey', 'Ready to join our community of successful authors? Let\'s make your publishing dream a reality.')}
                    className="h-14 sm:h-14 px-6 sm:px-8 rounded-xl bg-[#F4D03F] text-[#0B0B0B] font-semibold text-sm sm:text-base tracking-[0.2px] hover:bg-[#F8D26B] transition-all duration-300 flex items-center justify-center gap-2 group"
                  >
                    Discuss Project
                    <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </button>
                  <button 
                    onClick={openChat}
                    className="h-14 sm:h-14 px-6 sm:px-8 rounded-xl border-2 border-[#F4D03F] text-[#F4D03F] font-semibold text-sm sm:text-base tracking-[0.2px] hover:bg-[rgba(244,208,63,0.1)] transition-all duration-300 flex items-center justify-center gap-2 group"
                  >
                    Live Chat
                  </button>
                </div>
              </div>
              
              <div data-aos="fade-left">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800"
                    alt="Ready to Start Your Publishing Journey"
                    className="w-full h-64 sm:h-80 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Service Popup */}
      <ServicePopup
        isOpen={popupOpen}
        onClose={() => setPopupOpen(false)}
        serviceTitle={popupService.title}
        serviceDescription={popupService.description}
      />
    </div>
  );
};

export default AboutUs;
